import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BookOpen, Briefcase, CheckCircle2, Clock, DollarSign, ArrowRight, User, Circle, Gift, Copy, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/ioa_logo_dark.svg";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralName, setReferralName] = useState('');
  const [referralSending, setReferralSending] = useState(false);
  const [referralSent, setReferralSent] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/courses`).then(r => r.data),
      axios.get(`${API}/enrollments`, { withCredentials: true }).then(r => r.data).catch(() => []),
      axios.get(`${API}/progress`, { withCredentials: true }).then(r => r.data).catch(() => []),
      axios.get(`${API}/referral/code`, { withCredentials: true }).then(r => r.data).catch(() => null)
    ]).then(([c, e, p, ref]) => {
      setCourses(c);
      setEnrollments(e);
      setProgress(p);
      setReferralCode(ref);
    }).finally(() => setLoading(false));
  }, []);

  const handleEnroll = async (slug) => {
    setEnrolling(slug);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payments/create-checkout`, { course_slug: slug, origin_url: origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(null);
    }
  };

  const toggleModule = async (slug, moduleIndex, currentlyCompleted) => {
    try {
      const { data } = await axios.post(`${API}/progress/update`, {
        course_slug: slug,
        module_index: moduleIndex,
        completed: !currentlyCompleted
      }, { withCredentials: true });
      setProgress(prev => {
        const existing = prev.find(p => p.course_slug === slug);
        if (existing) {
          return prev.map(p => p.course_slug === slug ? { ...p, ...data } : p);
        }
        return [...prev, { course_slug: slug, ...data }];
      });
    } catch {}
  };

  const isEnrolled = (slug) => enrollments.some(e => e.course_slug === slug && (e.status === 'active' || e.status === 'paid'));
  const getProgress = (slug) => progress.find(p => p.course_slug === slug);
  const isModuleComplete = (slug, idx) => {
    const p = getProgress(slug);
    return p?.completed_modules?.includes(idx) || false;
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading dashboard...</div>;

  const totalProgress = enrollments.length > 0
    ? Math.round(progress.reduce((sum, p) => sum + (p.progress_pct || 0), 0) / Math.max(enrollments.length, 1))
    : 0;

  return (
    <div data-testid="student-dashboard" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Student Dashboard</p>
            <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Welcome, {user?.name || 'Student'}
            </h1>
            <p className="text-base text-zinc-300 mt-1">Institute of Analytics USA&trade;</p>
          </div>
          <Link to="/profile" data-testid="edit-profile-link" className="brand-btn text-sm px-5 py-2.5 flex items-center gap-2">
            <User className="w-4 h-4" strokeWidth={1.5} /> Edit Profile
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-[#262626] mb-12">
          <div className="p-6 border-r border-[#262626] bg-[#111111]">
            <BookOpen className="w-10 h-10 text-[#0077B6] mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{enrollments.length}</p>
            <p className="text-sm text-zinc-300">Enrolled Courses</p>
          </div>
          <div className="p-6 border-r border-[#262626] bg-[#111111]">
            <Clock className="w-10 h-10 text-[#0077B6] mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{enrollments.length * 20}hrs</p>
            <p className="text-sm text-zinc-300">Total Training Hours</p>
          </div>
          <div className="p-6 border-r border-[#262626] bg-[#111111]">
            <DollarSign className="w-10 h-10 text-[#F59E0B] mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>${enrollments.length * 595}</p>
            <p className="text-sm text-zinc-300">Invested</p>
          </div>
          <div className="p-6 bg-[#111111]">
            <CheckCircle2 className="w-10 h-10 text-green-400 mb-2" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{totalProgress}%</p>
            <p className="text-sm text-zinc-300">Overall Progress</p>
          </div>
        </div>

        {/* Enrolled Courses with Progress */}
        {enrollments.length > 0 && (
          <div className="mb-12">
            <h2 className="text-xl font-semibold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>My Courses — Module Progress</h2>
            <div className="space-y-4">
              {enrollments.map((enr, i) => {
                const course = courses.find(c => c.slug === enr.course_slug);
                const prog = getProgress(enr.course_slug);
                const pct = prog?.progress_pct || 0;
                return (
                  <div key={i} className="border border-[#262626] bg-[#111111]">
                    <div className="flex items-center justify-between p-5 border-b border-[#262626]">
                      <div>
                        <p className="font-semibold text-base text-white">{course?.title || enr.course_slug}</p>
                        <p className="text-sm text-zinc-300 mt-0.5">{pct}% complete &middot; {prog?.completed_modules?.length || 0}/10 modules</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32 h-2 bg-[#262626]">
                          <div className="h-2 bg-[#0077B6] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <Link to={`/learn/${enr.course_slug}`} data-testid={`start-learning-${enr.course_slug}`} className="brand-btn text-sm px-4 py-2 whitespace-nowrap">
                          Start Learning
                        </Link>
                      </div>
                    </div>
                    {course?.modules && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-0">
                        {course.modules.map((mod, j) => {
                          const done = isModuleComplete(enr.course_slug, j);
                          return (
                            <button
                              key={j}
                              onClick={() => toggleModule(enr.course_slug, j, done)}
                              data-testid={`progress-${enr.course_slug}-${j}`}
                              className={`p-3 border-r border-b border-[#262626] text-left transition-colors ${done ? 'bg-[#0077B6]/10' : 'hover:bg-[#1a1a1a]'}`}
                            >
                              <div className="flex items-start gap-2">
                                {done
                                  ? <CheckCircle2 className="w-8 h-8 text-[#0077B6] mt-0.5 flex-shrink-0" strokeWidth={2} />
                                  : <Circle className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                                }
                                <span className={`text-xs leading-tight ${done ? 'text-[#0077B6]' : 'text-zinc-400'}`}>
                                  Wk {j + 1}: {mod.split(' ').slice(0, 4).join(' ')}...
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Courses */}
        <h2 className="text-xl font-semibold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {enrollments.length > 0 ? 'Continue Your Learning' : 'Available Courses'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#262626]">
          {courses.map((course) => {
            const enrolled = isEnrolled(course.slug);
            return (
              <div key={course.slug} className="p-6 border-b border-r border-[#262626] bg-[#111111] flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl font-mono font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0{course.course_number}</span>
                    <p className="font-medium text-base text-white">{course.title}</p>
                  </div>
                  <p className="text-sm text-zinc-300">{course.hours}hrs / ${course.price}</p>
                </div>
                {enrolled ? (
                  <span className="text-sm font-mono text-green-400 bg-green-900/30 px-3 py-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Enrolled</span>
                ) : (
                  <button
                    onClick={() => handleEnroll(course.slug)}
                    disabled={enrolling === course.slug}
                    data-testid={`enroll-btn-${course.slug}`}
                    className="brand-btn text-sm px-4 py-2 disabled:opacity-50"
                  >
                    {enrolling === course.slug ? 'Processing...' : 'Enroll $' + course.price}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Referral Program */}
        <div className="mt-12 border border-[#262626] bg-[#111111] p-8">
          <div className="flex items-start gap-4 mb-6">
            <Gift className="w-10 h-10 text-[#F59E0B] flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Referral Program</p>
              <h2 className="text-xl font-bold tracking-tight text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Refer a Friend, They Get $50 Off
              </h2>
              <p className="text-base text-zinc-300 mt-2">Share your unique referral code. When someone enrolls using your code, they save $50 on their first course.</p>
            </div>
          </div>

          {referralCode && (
            <div className="mb-6">
              <label className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Your Referral Code</label>
              <div className="flex items-center gap-3">
                <div className="flex-1 px-5 py-3 bg-[#0A0A0A] border border-[#262626] text-lg font-mono text-[#0077B6] font-bold tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {referralCode.code}
                </div>
                <button
                  onClick={() => {
                    try { navigator.clipboard.writeText(referralCode.code); } catch { /* fallback */ const t = document.createElement('textarea'); t.value = referralCode.code; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
                    setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  data-testid="copy-referral-code"
                  className="brand-btn px-4 py-3 flex items-center gap-2 text-sm"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.5} /> {codeCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {referralCode.referrals?.length > 0 && (
                <p className="text-sm text-zinc-400 mt-2">{referralCode.referrals.length} referral(s) sent</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              data-testid="referral-name"
              value={referralName}
              onChange={e => setReferralName(e.target.value)}
              className="px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500"
              placeholder="Friend's name"
            />
            <input
              data-testid="referral-email"
              value={referralEmail}
              onChange={e => setReferralEmail(e.target.value)}
              type="email"
              className="px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500"
              placeholder="Friend's email"
            />
            <button
              onClick={async () => {
                if (!referralEmail || !referralName) return;
                setReferralSending(true);
                try {
                  await axios.post(`${API}/referral/send`, { referred_email: referralEmail, referred_name: referralName }, { withCredentials: true });
                  setReferralSent(true);
                  setReferralEmail('');
                  setReferralName('');
                  setTimeout(() => setReferralSent(false), 3000);
                } catch {}
                setReferralSending(false);
              }}
              disabled={referralSending || !referralEmail || !referralName}
              data-testid="send-referral"
              className="brand-btn-red text-base py-3 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} /> {referralSent ? 'Referral Sent!' : referralSending ? 'Sending...' : 'Send Referral'}
            </button>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 flex gap-4">
          <Link to="/jobs" className="brand-btn text-base px-6 py-3 flex items-center gap-2" data-testid="dashboard-jobs-link">
            <Briefcase className="w-4 h-4" strokeWidth={1.5} /> Browse Jobs
          </Link>
          <Link to="/profile" className="border-2 border-[#0077B6] text-[#0077B6] px-6 py-3 text-base font-medium hover:-translate-y-1 hover:shadow-[4px_4px_0px_#CC0000] transition-all duration-150 flex items-center gap-2">
            <User className="w-4 h-4" strokeWidth={1.5} /> My Profile
          </Link>
        </div>
      </div>
    </div>
  );
}
